/**
 * Даты и числа под текущий язык.
 *
 * Раньше каждый экран звал `toLocaleDateString('ru', …)` со своим набором опций,
 * и переключение языка их не касалось: у англоязычного клиента оставались
 * «13 авг» и «1 234,50». Форматы собраны сюда, чтобы правка была в одном месте.
 */
import i18n from '@/i18n';

/** BCP-47 под Intl. Языка ровно два, всё незнакомое считаем русским. */
export function intlLocale(lng?: string): string {
  return (lng ?? i18n.language) === 'en' ? 'en-GB' : 'ru-RU';
}

/**
 * Русский `short`-месяц приходит с точкой («13 авг.»), и в подписи под ником это
 * читается как обрыв строки. Точку убираем; en-GB её и так не ставит.
 */
function trimDots(s: string): string {
  return s.replace(/\.(?=\s|,|$)/g, '');
}

/** «13 авг» / «13 Aug». */
export function shortDate(value: string | Date, lng?: string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return trimDots(
    new Intl.DateTimeFormat(intlLocale(lng), { day: 'numeric', month: 'short' }).format(d),
  );
}

/** «13 авг 2026» / «13 Aug 2026» — без «г.» на конце. */
export function shortDateYear(value: string | Date, lng?: string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const out = new Intl.DateTimeFormat(intlLocale(lng), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
  return trimDots(out.replace(/\s*г\.?$/, ''));
}

/** «13.08.2026» / «13/08/2026» — компактная дата для списков. */
export function numericDate(value: string | Date, lng?: string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(intlLocale(lng), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/** «13 авг, 14:05» / «13 Aug, 14:05». */
export function shortDateTime(value: string | Date, lng?: string): string {
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return trimDots(
    new Intl.DateTimeFormat(intlLocale(lng), {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d),
  );
}

/** Разделители разрядов под язык: «1 234,5» против «1,234.5». */
export function formatNumber(value: number, lng?: string): string {
  return value.toLocaleString(intlLocale(lng));
}
